import React, { useEffect, useState, useMemo } from 'react';
import { Form, Button, Badge, InputGroup } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import LoaderOverlay from '../../Components/shared/Loader';
import AlertService from '../../services/utility/alertService';
import { getMasterRequestReport } from '../../services/api/masterRequestService';
import { getStatusConfig, getTypeLabel } from './masterRequestConfig';
import { TypeBadge } from './DynamicFields';
import formatDateForInput from '../../services/CommonServices/dateService';
import './MasterRequest.css';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
    const config = getStatusConfig(status);
    return <Badge bg={config.variant}>{config.label}</Badge>;
};

const getExportTimestamp = () => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    return (
        pad(now.getDate()) +
        pad(now.getMonth() + 1) +
        now.getFullYear() +
        pad(now.getHours()) +
        pad(now.getMinutes()) +
        pad(now.getSeconds())
    );
};

function MasterRequestReportPage() {
    const [requests, setRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(formatDateForInput(new Date()));
    const [toDate, setToDate] = useState(formatDateForInput(new Date()));

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await getMasterRequestReport(fromDate, toDate);
            setRequests(response.data || []);
        } catch (error) {
            console.error('Error fetching master data request report:', error);
            if (error.response?.status === 403) {
                AlertService.errorAlert('You do not have permission to view this report.');
            } else {
                AlertService.errorAlert('Unable to load the report. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return requests;

        return requests.filter((item) => [
            String(item.ID),
            item.REQ_NAME,
            getTypeLabel(item.REQ_TYPE),
            item.EMP_NAME,
            item.CUS_CODE,
            item.CUS_NAME,
            item.REQ_STATUS,
            item.CREATED_CODE,
            formatDate(item.REQ_DATE)
        ].some(field => (field || '').toString().toLowerCase().includes(term)));
    }, [requests, searchTerm]);

    const exportReport = () => {
        // Exports what's on screen (search-filtered), not the full fetched set.
        const rows = filteredData.map((item) => ({
            'ID': item.ID,
            'Date': formatDate(item.REQ_DATE),
            'Type': getTypeLabel(item.REQ_TYPE),
            'Name': item.REQ_NAME,
            'Raised By': item.EMP_NAME || '-',
            'Customer': item.CUS_NAME || '-',
            'Status': item.REQ_STATUS,
            'Created Code': item.CREATED_CODE || '-',
            'Created Name': item.CREATED_NAME || '-',
            'Closed By': item.CLOSED_BY || '-',
            'Closed On': formatDate(item.CLOSED_ON),
            'Remarks': item.ACTION_COMMENT || '-'
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        saveAs(blob, `CustomerItemRequestReport-${getExportTimestamp()}.xlsx`);
    };

    return (
        <div className="card aon-card master-request-page">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <h4 className="mb-0">Customer/Item Request Report</h4>
            </div>

            <div className="card-body aon-card-body">
                <div className="d-flex align-items-end flex-wrap mb-3" style={{ gap: '1rem' }}>
                    <Form.Group controlId="fromDate">
                        <Form.Label>From</Form.Label>
                        <Form.Control
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId="toDate">
                        <Form.Label>To</Form.Label>
                        <Form.Control
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Form.Group>
                    <Button variant="primary" onClick={fetchReport}>
                        Submit
                    </Button>
                    <Button variant="outline-primary" onClick={exportReport} disabled={filteredData.length === 0}>
                        Export
                    </Button>
                </div>

                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Search requests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search requests"
                    />
                    {searchTerm && (
                        <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                            Clear
                        </Button>
                    )}
                </InputGroup>

                {filteredData.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        {requests.length === 0
                            ? 'No requests were raised in this date range.'
                            : 'No requests match your search.'}
                    </div>
                ) : (
                    <>
                        <div className="d-md-none">
                            {filteredData.map((item) => (
                                <div className="mrq-card" key={item.ID}>
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="mrq-card-title">{item.REQ_NAME}</div>
                                        <StatusBadge status={item.REQ_STATUS} />
                                    </div>
                                    <div className="mrq-card-meta">
                                        #{item.ID} &middot; <TypeBadge reqType={item.REQ_TYPE} /> &middot; {formatDate(item.REQ_DATE)}
                                    </div>
                                    <div className="mrq-card-meta">Raised by: {item.EMP_NAME || '-'}</div>
                                    {item.CUS_NAME && (
                                        <div className="mrq-card-meta">Customer: {item.CUS_NAME}</div>
                                    )}
                                    {item.CREATED_CODE && (
                                        <div className="mrq-card-created">
                                            Created: {item.CREATED_CODE}
                                            {item.CREATED_NAME ? ' - ' + item.CREATED_NAME : ''}
                                        </div>
                                    )}
                                    {item.ACTION_COMMENT && (
                                        <div className="mrq-card-meta">Remarks: {item.ACTION_COMMENT}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="d-none d-md-block sf-bs-data-table">
                            <div className="table-responsive">
                                <table
                                    className="table table-striped table-bordered aon-booking-table"
                                    style={{ width: '100%' }}
                                >
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Name</th>
                                            <th>Raised By</th>
                                            <th>Customer</th>
                                            <th>Status</th>
                                            <th>Created Code</th>
                                            <th>Created Name</th>
                                            <th>Closed By</th>
                                            <th>Closed On</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item) => (
                                            <tr key={item.ID}>
                                                <td>{item.ID}</td>
                                                <td>{formatDate(item.REQ_DATE)}</td>
                                                <td><TypeBadge reqType={item.REQ_TYPE} /></td>
                                                <td>{item.REQ_NAME}</td>
                                                <td>{item.EMP_NAME || '-'}</td>
                                                <td>{item.CUS_NAME || '-'}</td>
                                                <td><StatusBadge status={item.REQ_STATUS} /></td>
                                                <td>{item.CREATED_CODE || '-'}</td>
                                                <td>{item.CREATED_NAME || '-'}</td>
                                                <td>{item.CLOSED_BY || '-'}</td>
                                                <td>{formatDate(item.CLOSED_ON)}</td>
                                                <td>{item.ACTION_COMMENT || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default MasterRequestReportPage;
